import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import DatatypeDisplay from '../../core/DatatypeDisplay';
import Errored from '../../core/Errored';
import TitledPanel from '../../core/TitledPanel';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import OntologyRelationshipsDisplay from './OntologyRelationshipsDisplay';

const CLASSES_TO_INCLUDE = [
    "http://www.w3.org/2004/02/skos/core#Concept",
    "http://www.w3.org/2008/05/skos-xl#Label",
    "http://www.w3.org/2004/02/skos/core#ConceptScheme",
    "http://www.w3.org/2004/02/skos/core#Collection",
    "http://www.w3.org/2004/02/skos/core#OrderedCollection"
];

class AssociatedPropertyDisplay extends React.Component {

    render() {

        const { data, defaultLang, resourceClasses, isReadOnly, components, projectGraph, associatedProjectGraph, linkedData, info,
            onAssociatedPropertyDelete, onAssociatedPropertyUpdate, viewLang, projectLang, searchClasses,
            associatedProject, dialog, addNewResource, useWorkflow,
            schemeIRI, resourceData, projectIRI, id, includeExistingResource, addNewLiteral, addNewTypedLiteral, context } = this.props;

        const resourceTypes = Array.isArray(data.type) ? data.type : [data.type];
        const isDeprecated = data.deprecated;
        const isFunctional = resourceTypes.includes("FunctionalProperty");
        const property = id;
        const currentCardinality = resourceData[property] ?
            Array.isArray(resourceData[property]) ?
                resourceData[property].length
                :
                1
            :
            0;
        const valuesList = (resourceTypes.includes("DatatypeProperty") || resourceTypes.includes("AnnotationProperty")) ?
            (
                resourceData.hasOwnProperty(property) && resourceData[property] !== undefined ?
                    (
                        Array.isArray(resourceData[property]) ? resourceData[property] : [resourceData[property]]
                    )
                    :
                    []
            )
            :
            GS.getIRIsForProperty(resourceData, property);

        const title = data.hasOwnProperty("label") ?
            data.label[defaultLang] ?
                data.label[defaultLang]
                :
                data.label["und"] ?
                    data.label["und"]
                    :
                    data.id
            :
            data.id;


        // We only display properties for which the domain is one of its classes (or superclasses)
        let isValidForDomain;
        try {
            const inferredResourceClasses = [].concat.apply([], resourceClasses
                .map(classIRI => CLASSES_TO_INCLUDE.includes(classIRI) ?
                    [classIRI]
                    :
                    GS.getSuperClasses(classIRI, associatedProjectGraph)));
            isValidForDomain = !data.domain || data.domain.length === 0
                || (data.domain.includes("http://www.w3.org/2002/07/owl#Thing") && data.domain.length === 1)
                || data.domain.some(domainClass => inferredResourceClasses.includes(domainClass));
            // If no existing values don't display
            if (!isValidForDomain && valuesList.length === 0) return null;
        }
        catch (e) {
            return <Errored data={e} />;
        }

        if (data._notAvailable) {
            return <Grid item xs={12} md={6}>
                <TitledPanel dataTest={"deleted-" + id} title={id}>
                    <Typography variant="body2">This has been deleted but has not been removed from associated project set up, or you do not have permission.</Typography>
                </TitledPanel>
            </Grid>;
        }

        // If range includes owl:Thing then essentially it's everything so we just remove the range restriction
        return (
            <Grid item xs={12} md={6} data-test={id}>
                {(resourceTypes.includes("DatatypeProperty") || resourceTypes.includes("AnnotationProperty")) &&
                    <DatatypeDisplay
                        title={title}
                        info={info}
                        source={resourceData.id}
                        deprecated={isDeprecated}
                        datatype={data.range && data.range.length > 0 ?
                            data.range[0]
                            :
                            this.props.overrideNullDatatype ?
                                ""
                                :
                                undefined}
                        defaultLang={defaultLang}
                        viewLang={viewLang}
                        projectLang={projectLang}
                        property={id}
                        values={valuesList}
                        context={context}
                        searchClasses={searchClasses}
                        projectIRI={projectIRI}
                        onDelete={onAssociatedPropertyDelete}
                        graph={projectGraph}
                        associatedProjectGraph={associatedProjectGraph}
                        onUpdate={onAssociatedPropertyUpdate}
                        isReadOnly={isReadOnly}
                        canAddValues={isValidForDomain}
                        addNewResource={addNewResource ? addNewResource(id, associatedProjectGraph, "@id") : null}
                        cardinalityAtMax={(isFunctional && currentCardinality > 0) || (info.maxCount && currentCardinality >= info.maxCount)}
                        exceedsCardinality={(isFunctional && currentCardinality > 1) || (info.maxCount && currentCardinality > info.maxCount)}
                        onAddLiteral={addNewLiteral(data.id, associatedProjectGraph)}
                        onAddTypedLiteral={addNewTypedLiteral(data.id, associatedProjectGraph, data.domain ? [...data.domain] : null)} />
                }
                {resourceTypes.includes("ObjectProperty") &&
                    <OntologyRelationshipsDisplay
                        dialog={dialog}
                        useWorkflow={useWorkflow}
                        title={title}
                        info={info}
                        deprecated={isDeprecated}
                        resourceIRI={resourceData.id}
                        projectIRI={projectIRI}
                        source={id}
                        searchTypes={data.type.includes("AsymmetricProperty") ? ["Asymmetric"] : []}
                        graph={projectGraph}
                        allowEmpty={"inComponent" in info
                            || (data.range && (data.range.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme")
                                || data.range.includes("http://www.w3.org/2004/02/skos/core#Collection")
                                || data.range.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection"))) ?
                            true
                            :
                            false}
                        allowIRI={"inComponent" in info ? false : true}
                        matching={"inComponent" in info ? false : true}
                        linkedData={linkedData}
                        associatedProject={associatedProject}
                        defaultLang={defaultLang}
                        viewLang={viewLang}
                        components={components}
                        associatedProjectGraph={associatedProjectGraph}
                        searchClasses={data.range && !data.range.includes("http://www.w3.org/2002/07/owl#Thing") ? data.range : []}
                        schemeIRI={("inComponent" in info) ? info.inComponent : schemeIRI}
                        property={id}
                        values={valuesList}
                        isReadOnly={isReadOnly}
                        canAddValues={isValidForDomain}
                        cardinalityAtMax={(isFunctional && currentCardinality > 0) || (info.maxCount && currentCardinality >= info.maxCount)}
                        exceedsCardinality={(isFunctional && currentCardinality > 1) || (info.maxCount && currentCardinality > info.maxCount)}
                        includeExistingResource={includeExistingResource ? includeExistingResource(property, associatedProjectGraph) : null} />
                }
            </Grid>
        );
    }
}

AssociatedPropertyDisplay.defaultProps = {
    info: {},
    searchClasses: [],
    overrideNullDatatype: false,
    useWorkflow: false
};

AssociatedPropertyDisplay.propTypes = {
    dialog: PropTypes.object,
    id: PropTypes.string.isRequired,
    info: PropTypes.object,
    projectIRI: PropTypes.string.isRequired,
    schemeIRI: PropTypes.string,
    context: PropTypes.string,
    useWorkflow: PropTypes.bool,
    isReadOnly: PropTypes.bool.isRequired,
    components: PropTypes.array,
    viewLang: PropTypes.string,
    associatedProject: PropTypes.array,
    projectLang: PropTypes.array.isRequired,
    addNewLiteral: PropTypes.func.isRequired,
    addNewTypedLiteral: PropTypes.func.isRequired,
    linkedData: PropTypes.array,
    includeExistingResource: PropTypes.func,
    addNewResource: PropTypes.func,
    searchClasses: PropTypes.array,
    onAssociatedPropertyDelete: PropTypes.func.isRequired,
    onAssociatedPropertyUpdate: PropTypes.func.isRequired,
    graph: PropTypes.string.isRequired,
    resourceData: PropTypes.object.isRequired,
    defaultLang: PropTypes.string.isRequired,
    projectGraph: PropTypes.string.isRequired,
    associatedProjectGraph: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    resourceClasses: PropTypes.array.isRequired
};

export default withGS(AssociatedPropertyDisplay);